import React, { useState, useEffect, useMemo } from "react";
import { InputNumber } from "primereact/inputnumber";
import { RadioButton } from "primereact/radiobutton";
import SimpleModal from "./SimpleModal";
import PropTypes from "prop-types";
import { useChartsStore } from "../../store/chartStore";
import { isFeatureEnabled } from "../../data/Services/settings";

/**
 * Calculate auto brick size from chart data
 * Uses the same logic as the renko calculator
 */
function calculateAutoBrickSize(data) {
  if (!data || data.length === 0) return 0.5;

  const sampleSize = Math.min(300, data.length);
  const recentData = data.slice(data.length - sampleSize);

  let minPrice = Infinity;
  let maxPrice = -Infinity;

  recentData.forEach((d) => {
    const prices = [d.close, d.high, d.low].filter(
      (p) => p != null && !isNaN(p),
    );
    prices.forEach((p) => {
      if (p < minPrice) minPrice = p;
      if (p > maxPrice) maxPrice = p;
    });
  });

  const priceRange = maxPrice - minPrice;

  if (!isFinite(priceRange) || priceRange <= 0) {
    return 0.5;
  }

  const targetBricks = 20; // Matches ChartIQ observed behavior
  const calculatedSize = priceRange / targetBricks;

  if (!isFinite(calculatedSize) || calculatedSize <= 0) {
    return 0.5;
  }

  return calculatedSize;
}

/**
 * Calculate ATR (Average True Range) for the data
 * Uses Simple Moving Average of the last N True Ranges
 * This gives current volatility based on recent data only
 */
function calculateATR(data, period = 14) {
  if (!data || data.length < period + 1) {
    return null;
  }

  // Calculate True Range for the last (period) bars only
  const startIndex = Math.max(1, data.length - period);
  const trueRanges = [];

  for (let i = startIndex; i < data.length; i++) {
    const current = data[i];
    const previous = data[i - 1];

    const highLow = current.high - current.low;
    const highPrevClose = Math.abs(current.high - previous.close);
    const lowPrevClose = Math.abs(current.low - previous.close);

    const trueRange = Math.max(highLow, highPrevClose, lowPrevClose);
    trueRanges.push(trueRange);
  }

  if (trueRanges.length === 0) {
    return null;
  }

  // Simple Moving Average of True Ranges
  const sum = trueRanges.reduce((acc, tr) => acc + tr, 0);
  const atr = sum / trueRanges.length;

  return atr;
}

/**
 * Renko Settings Modal
 *
 * Three modes:
 * - auto: Calculate brick size to show ~20 bricks
 * - fixed: User-defined brick size
 * - atr: ATR-based brick size with period and multiplier
 */
const RenkoSettingsModal = ({ visible, onHide, chartId }) => {
  const chart = useChartsStore((state) =>
    state.charts.find((c) => c.id === chartId),
  );
  const chartData = useChartsStore((state) => state.getData(chartId));
  const updateChartConfig = useChartsStore((state) => state.updateChartConfig);

  // Get current renko config or use defaults
  const currentConfig = chart?.renkoConfig || {
    mode: "auto",
    brickSize: null,
    atrPeriod: 14,
    atrMultiplier: 1.0,
  };

  // State
  const [mode, setMode] = useState(currentConfig.mode || "auto");
  const [brickSize, setBrickSize] = useState(currentConfig.brickSize || 0.5);
  const [atrPeriod, setAtrPeriod] = useState(currentConfig.atrPeriod || 14);
  const [atrMultiplier, setAtrMultiplier] = useState(
    currentConfig.atrMultiplier || 1.0,
  );
  // Motor de cálculo (#82) — antes só existia no pill do gráfico, e este modal
  // apenas PRESERVAVA o valor. Quem configurava pela engrenagem da navbar não
  // tinha como trocar de motor (#435). Mesma flag de kill switch do pill.
  const [engine, setEngine] = useState(currentConfig.engine || "chartiq");

  // Calculate auto brick size from current data
  const autoBrickSize = useMemo(() => {
    return calculateAutoBrickSize(chartData);
  }, [chartData]);

  // Calculate ATR brick size from current data
  const atrBrickSize = useMemo(() => {
    const atrValue = calculateATR(chartData, atrPeriod);
    if (atrValue && isFinite(atrValue) && atrValue > 0) {
      return atrValue * atrMultiplier;
    }
    return null;
  }, [chartData, atrPeriod, atrMultiplier]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      const config = chart?.renkoConfig || {
        mode: "auto",
        brickSize: null,
        atrPeriod: 14,
        atrMultiplier: 1.0,
      };
      setMode(config.mode || "auto");
      setBrickSize(config.brickSize || autoBrickSize);
      setAtrPeriod(config.atrPeriod || 14);
      setAtrMultiplier(config.atrMultiplier || 1.0);
      setEngine(config.engine || "chartiq");
    }
  }, [visible, chart?.renkoConfig, autoBrickSize]);

  const handleSave = () => {
    const renkoConfig = {
      mode,
      brickSize: mode === "fixed" ? brickSize : null,
      atrPeriod,
      atrMultiplier,
      engine,
    };

    updateChartConfig(chartId, { renkoConfig });
    onHide();
  };

  // Get the effective brick size for display
  const getEffectiveBrickSize = () => {
    switch (mode) {
      case "auto":
        return autoBrickSize;
      case "fixed":
        return brickSize;
      case "atr":
        return atrBrickSize || autoBrickSize;
      default:
        return autoBrickSize;
    }
  };

  return (
    <SimpleModal
      visible={visible}
      onHide={onHide}
      onConfirm={handleSave}
      title="Configurações do Renko"
      confirmLabel="Aplicar"
      cancelLabel="Cancelar"
      size="small"
      contentStyle={{ overflow: "visible", minHeight: "320px" }}
    >
      <div
        className="p-fluid"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
          padding: "0.5rem 0",
        }}
      >
        {/* Mode Selection */}
        <div className="p-field" style={{ marginBottom: 0 }}>
          <label
            style={{
              marginBottom: "0.75rem",
              display: "block",
              fontWeight: 600,
            }}
          >
            Método de Cálculo
          </label>

          {/* Auto Mode */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <RadioButton
              inputId="modeAuto"
              name="mode"
              value="auto"
              checked={mode === "auto"}
              onChange={(e) => setMode(e.value)}
            />
            <label htmlFor="modeAuto" style={{ cursor: "pointer" }}>
              Automático
            </label>
          </div>

          {/* Fixed Mode */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <RadioButton
              inputId="modeFixed"
              name="mode"
              value="fixed"
              checked={mode === "fixed"}
              onChange={(e) => setMode(e.value)}
            />
            <label htmlFor="modeFixed" style={{ cursor: "pointer" }}>
              Tamanho Fixo
            </label>
          </div>

          {/* ATR Mode */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <RadioButton
              inputId="modeAtr"
              name="mode"
              value="atr"
              checked={mode === "atr"}
              onChange={(e) => setMode(e.value)}
            />
            <label htmlFor="modeAtr" style={{ cursor: "pointer" }}>
              ATR (Average True Range)
            </label>
          </div>
        </div>

        {/* Mode-specific options */}
        {mode === "auto" && (
          <div
            className="p-field"
            style={{
              marginBottom: 0,
              padding: "0.75rem",
              backgroundColor: "var(--surface-100)",
              borderRadius: "6px",
            }}
          >
            <small className="p-text-secondary">
              Calcula o tamanho do tijolo para mostrar ~20 tijolos na tela.
              <br />
              <strong>Valor calculado: {autoBrickSize.toFixed(4)}</strong>
            </small>
          </div>
        )}

        {mode === "fixed" && (
          <div className="p-field" style={{ marginBottom: 0 }}>
            <label
              htmlFor="brickSize"
              style={{
                marginBottom: "0.5rem",
                display: "block",
                fontWeight: 500,
              }}
            >
              Tamanho do Tijolo
            </label>
            <InputNumber
              id="brickSize"
              value={brickSize}
              onValueChange={(e) => setBrickSize(e.value)}
              minFractionDigits={0}
              maxFractionDigits={4}
              min={0.0001}
              step={0.1}
              style={{ width: "100%" }}
              data-evo-id="renkosettingsmodal-input-bricksize"
            />
            <small
              className="p-text-secondary"
              style={{ display: "block", marginTop: "0.5rem" }}
            >
              Tamanho fixo de cada tijolo Renko em pontos
            </small>
          </div>
        )}

        {mode === "atr" && (
          <>
            <div className="p-field" style={{ marginBottom: 0 }}>
              <label
                htmlFor="atrPeriod"
                style={{
                  marginBottom: "0.5rem",
                  display: "block",
                  fontWeight: 500,
                }}
              >
                Período do ATR
              </label>
              <InputNumber
                id="atrPeriod"
                value={atrPeriod}
                onValueChange={(e) => setAtrPeriod(e.value)}
                min={1}
                max={100}
                step={1}
                showButtons
                style={{ width: "100%" }}
                data-evo-id="renkosettingsmodal-input-atrperiod"
              />
              <small
                className="p-text-secondary"
                style={{ display: "block", marginTop: "0.5rem" }}
              >
                Número de períodos para calcular o ATR (padrão: 14)
              </small>
            </div>

            <div className="p-field" style={{ marginBottom: 0 }}>
              <label
                htmlFor="atrMultiplier"
                style={{
                  marginBottom: "0.5rem",
                  display: "block",
                  fontWeight: 500,
                }}
              >
                Multiplicador
              </label>
              <InputNumber
                id="atrMultiplier"
                value={atrMultiplier}
                onValueChange={(e) => setAtrMultiplier(e.value)}
                minFractionDigits={1}
                maxFractionDigits={2}
                min={0.1}
                max={10}
                step={0.1}
                showButtons
                style={{ width: "100%" }}
                data-evo-id="renkosettingsmodal-input-atrmultiplier"
              />
              <small
                className="p-text-secondary"
                style={{ display: "block", marginTop: "0.5rem" }}
              >
                Multiplicador do ATR (1.0 = 100% do ATR)
              </small>
            </div>

            <div
              className="p-field"
              style={{
                marginBottom: 0,
                padding: "0.75rem",
                backgroundColor: "var(--surface-100)",
                borderRadius: "6px",
              }}
            >
              <small className="p-text-secondary">
                O tamanho do tijolo se ajusta automaticamente à volatilidade.
                <br />
                <strong>
                  ATR({atrPeriod}):{" "}
                  {atrBrickSize ? atrBrickSize.toFixed(4) : "Calculando..."}
                </strong>
              </small>
            </div>
          </>
        )}

        {/* Motor de cálculo — mesmas opções e mesma flag do pill do gráfico,
            para os dois caminhos configurarem exatamente a mesma coisa. */}
        {isFeatureEnabled("renkoEngineSelector") && (
          <div className="p-field" style={{ marginBottom: 0 }}>
            <label
              style={{
                marginBottom: "0.5rem",
                display: "block",
                fontWeight: 500,
              }}
            >
              Motor de cálculo
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <RadioButton
                inputId="engineChartIQ"
                name="engine"
                value="chartiq"
                checked={engine !== "legacy"}
                onChange={(e) => setEngine(e.value)}
                data-evo-id="renkosettingsmodal-radio-engine-chartiq"
              />
              <label htmlFor="engineChartIQ" style={{ cursor: "pointer" }}>
                Padrão (HtmlCharts5)
              </label>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <RadioButton
                inputId="engineLegacy"
                name="engine"
                value="legacy"
                checked={engine === "legacy"}
                onChange={(e) => setEngine(e.value)}
                data-evo-id="renkosettingsmodal-radio-engine-legacy"
              />
              <label htmlFor="engineLegacy" style={{ cursor: "pointer" }}>
                Anterior (WebCharts26)
              </label>
            </div>
          </div>
        )}

        {/* Preview of effective brick size */}
        <div
          style={{
            marginTop: "0.5rem",
            padding: "0.75rem",
            backgroundColor: "var(--primary-color)",
            color: "var(--primary-color-text)",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          <strong>
            Tamanho do Tijolo: {getEffectiveBrickSize().toFixed(4)}
          </strong>
        </div>
      </div>
    </SimpleModal>
  );
};

RenkoSettingsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  chartId: PropTypes.string,
};

export default RenkoSettingsModal;
